import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useLang } from '../i18n/LanguageContext';
import { depositText } from '../i18n/pages/deposit';
import { DepositQueueItem, getOpenDeposits } from '../api.deposit';

/**
 * Deposits needing the manager, on the dashboard.
 *
 * <p>Sorted by <b>deadline, not by date</b>, which is the whole reason this is a list rather than a
 * count. Time is the scarce thing here: a hold running out tomorrow outranks a stay that ended a
 * week ago but still has days left on it, and neither of those orderings falls out of the check-out
 * date.
 *
 * <p>Two different jobs share the list on purpose. <i>No deposit</i> is a guest arriving who is not
 * covered — the only row where acting early still changes the outcome. <i>Decide</i> is money
 * already held. Mixing them is right because both are the same question to somebody standing in a
 * corridor with a phone: what needs me today?
 */
const DepositQueue: React.FC = () => {
  const { lang } = useLang();
  const t = depositText[lang];

  const [rows, setRows] = useState<DepositQueueItem[]>([]);

  useEffect(() => {
    // A dashboard that cannot load this block is still a perfectly good dashboard: stay silent
    // rather than putting an error where an optional section would have been.
    getOpenDeposits().then(setRows).catch(() => setRows([]));
  }, []);

  if (rows.length === 0) return null;

  const money = (v: number, currency: string) =>
    `${v.toLocaleString(lang === 'en' ? 'en-GB' : lang)} ${currency}`;

  /** How long is left, in the coarsest unit that is still honest. */
  const countdown = (iso: string | null): { text: string; urgent: boolean; past: boolean } => {
    if (!iso) return { text: '—', urgent: false, past: false };
    const ms = new Date(iso).getTime() - Date.now();
    if (ms <= 0) return { text: t.overdue, urgent: true, past: true };
    const hours = Math.floor(ms / 3_600_000);
    if (hours < 24) return { text: `${hours}h`, urgent: true, past: false };
    const days = Math.floor(hours / 24);
    return { text: `${days}d ${hours % 24}h`, urgent: days < 2, past: false };
  };

  return (
    <div style={styles.wrap}>
      <div style={styles.head}>
        <span style={styles.title}>🔒 {t.queueTitle}</span>
        <span style={styles.count}>{rows.length}</span>
      </div>
      <p style={styles.intro}>{t.queueIntro}</p>

      <div style={styles.rows}>
        {rows.map(row => {
          const held = row.status === 'HELD';
          const clock = countdown(row.deadlineAt);
          // An uncovered arrival is always urgent — there is a person travelling towards a door.
          const urgent = clock.urgent || !held;

          return (
            <Link key={row.depositId} to={`/reservations/${row.reservationId}`} style={styles.row}>
              <span style={{
                ...styles.stripe,
                background: urgent ? '#b91c1c' : held ? '#166534' : '#b45309',
              }} />

              <span style={styles.main}>
                <span style={styles.who}>{row.guestName}</span>
                {/* The date that explains the row: a held deposit is about a stay that has ended,
                    an uncovered one about a stay that has not started. */}
                <span style={styles.meta}>
                  {row.apartmentName} · {held ? row.checkOutDate : row.checkInDate}
                </span>
                {row.lastHoldError && <span style={styles.err}>{row.lastHoldError}</span>}
              </span>

              <span style={styles.right}>
                <span style={{
                  ...styles.chip,
                  ...(held ? styles.chipAmber : styles.chipRed),
                }}>
                  {held ? t.needsDecision : t.noDeposit}
                </span>
                <span style={styles.amount}>{money(row.amount, row.currency)}</span>
                <span style={styles.deadline}>
                  <span style={{ ...styles.clock, color: urgent ? '#b91c1c' : '#475569' }}>
                    {clock.text}
                  </span>
                  <span style={styles.clockLabel}>
                    {held ? t.holdExpires : t.toArrival}
                  </span>
                </span>
              </span>
            </Link>
          );
        })}
      </div>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  wrap: {
    marginBottom: 16, padding: '14px 16px', borderRadius: 11,
    background: '#fff', border: '1px solid #e2e8f0',
  },
  head: { display: 'flex', alignItems: 'baseline', gap: 9, flexWrap: 'wrap' },
  title: { fontWeight: 700, fontSize: '0.98rem', color: '#1e293b' },
  count: {
    fontSize: '0.72rem', fontWeight: 700, padding: '1px 8px', borderRadius: 99,
    background: '#fffbeb', color: '#b45309', border: '1px solid #fcd34d',
    fontVariantNumeric: 'tabular-nums',
  },
  intro: { fontSize: '0.78rem', color: '#64748b', margin: '3px 0 12px' },
  rows: { display: 'flex', flexDirection: 'column', gap: 7 },
  row: {
    display: 'grid', gridTemplateColumns: '4px 1fr auto', gap: '0 12px', alignItems: 'center',
    border: '1px solid #e2e8f0', borderRadius: 10, overflow: 'hidden',
    textDecoration: 'none', color: 'inherit', background: '#fff',
  },
  stripe: { alignSelf: 'stretch', width: 4 },
  main: { display: 'flex', flexDirection: 'column', gap: 2, padding: '10px 0', minWidth: 0 },
  who: { fontWeight: 650, fontSize: '0.88rem', color: '#0f172a' },
  meta: { fontSize: '0.76rem', color: '#64748b' },
  err: { fontSize: '0.72rem', color: '#b91c1c', marginTop: 2 },
  right: {
    display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
    justifyContent: 'flex-end', padding: '10px 13px 10px 0',
  },
  chip: {
    fontSize: '0.64rem', fontWeight: 700, letterSpacing: '.045em', textTransform: 'uppercase',
    padding: '3px 8px', borderRadius: 99, whiteSpace: 'nowrap',
  },
  chipAmber: { background: '#fffbeb', color: '#b45309', border: '1px solid #fcd34d' },
  chipRed: { background: '#fef2f2', color: '#b91c1c', border: '1px solid #fecaca' },
  amount: { fontSize: '0.85rem', fontWeight: 650, color: '#1e293b', fontVariantNumeric: 'tabular-nums' },
  deadline: { display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 1, minWidth: 78 },
  clock: { fontSize: '0.85rem', fontWeight: 700, fontVariantNumeric: 'tabular-nums' },
  clockLabel: { fontSize: '0.63rem', letterSpacing: '.04em', textTransform: 'uppercase', color: '#94a3b8' },
};

export default DepositQueue;
